<template>
  <div>
    <div v-if="errorMessage" class="alert alert-danger py-2">
      {{ errorMessage }}
    </div>

    <form class="site-form" @submit.prevent="handleSubmit">
      <template v-if="isEditMode">
        <PrimevueTabView v-model:activeIndex="activeEditTab" class="site-form-tabs">
          <PrimevueTabPanel header="Identité">
            <section class="site-form-section">
              <div class="section-heading">
                <span>Étape 1</span>
                <h6>Identité et présentation</h6>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-5">
                  <label class="form-label small fw-semibold">Nom du site / boutique *</label>
                  <PrimevueInputText v-model.trim="form.name" class="w-100" placeholder="Ex: Suite241 Libreville" />
                  <small class="text-muted">
                    S’affiche dans la navbar et sur les documents (tickets, factures).
                  </small>
                </div>

                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Code</label>
                  <PrimevueInputText v-model.trim="form.code" class="w-100" placeholder="Ex: LBV1" />
                  <small class="text-muted">
                    Code interne pour identifier rapidement le site (optionnel).
                  </small>
                </div>

                <div class="col-md-4">
                  <label class="form-label small fw-semibold">Type de site *</label>
                  <PrimevueDropdown
                    v-model="form.site_type"
                    :options="siteTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="w-100"
                  />
                  <small class="text-muted">
                    Utilisé pour organiser et présenter la boutique.
                  </small>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label small fw-semibold">Slogan</label>
                <PrimevueInputText v-model.trim="form.slogan" class="w-100" placeholder="Ex: La qualité au meilleur prix" />
                <small class="text-muted">
                  S’affiche sur le site / pages publiques (et éventuellement sur certains documents).
                </small>
              </div>

              <div class="card border-0 shadow-sm mb-0">
                <div class="card-body">
                  <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <h6 class="mb-1">
                        <i class="fa fa-image me-1"></i>
                        Image de couverture
                      </h6>
                      <small class="text-muted">
                        Elle sera affichée sur la carte du site dans la grille.
                      </small>
                    </div>
                  </div>

                  <div class="row g-3 align-items-center mt-1">
                    <div class="col-md-7">
                      <label class="form-label small fw-semibold">Fichier de couverture</label>
                      <input
                        type="file"
                        class="form-control"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        :disabled="isSubmitting"
                        @change="onCoverSelected"
                      />
                      <small class="text-muted d-block mt-1">
                        Formats acceptés : JPG, PNG, WebP, GIF. Taille maximale : 10 Mo.
                      </small>
                    </div>

                    <div class="col-md-5">
                      <div v-if="coverPreviewUrl || form.cover_image_url" class="cover-preview">
                        <img :src="coverPreviewUrl || form.cover_image_url" alt="Aperçu couverture" />
                      </div>
                      <div v-else class="cover-preview cover-preview-empty">
                        <i class="fa fa-image"></i>
                        <span>Aucune image</span>
                      </div>

                      <button
                        v-if="coverPreviewUrl || form.cover_image_url"
                        type="button"
                        class="btn btn-outline-danger btn-sm mt-2"
                        :disabled="isSubmitting"
                        @click="clearCoverImage"
                      >
                        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
                        Retirer la couverture
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </PrimevueTabPanel>

          <PrimevueTabPanel header="Coordonnées">
            <section class="site-form-section">
              <div class="section-heading">
                <span>Étape 2</span>
                <h6>Coordonnées et adresse</h6>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">Téléphone *</label>
                  <PrimevueInputText
                    v-model.trim="form.phone"
                    class="w-100"
                    type="tel"
                    placeholder="Ex: +241 xx xx xx xx"
                    @input="sanitizePhoneField"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">WhatsApp</label>
                  <PrimevueInputText
                    v-model.trim="form.whatsapp_phone"
                    class="w-100"
                    type="tel"
                    placeholder="Ex: +241 xx xx xx xx"
                    @input="sanitizeWhatsAppField"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">Email</label>
                  <PrimevueInputText v-model.trim="form.email" type="email" class="w-100" placeholder="Ex: contact@boutique.ga" />
                </div>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label small fw-semibold">Adresse *</label>
                  <PrimevueInputText v-model.trim="form.address" class="w-100" placeholder="Adresse complète" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Ville</label>
                  <PrimevueInputText v-model.trim="form.city" class="w-100" placeholder="Ex: Libreville" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Pays</label>
                  <PrimevueInputText v-model.trim="form.country" class="w-100" placeholder="Ex: Gabon" />
                </div>
              </div>
            </section>
          </PrimevueTabPanel>

          <PrimevueTabPanel header="Paramètres">
            <section class="site-form-section">
              <div class="section-heading">
                <span>Étape 3</span>
                <h6>Devise, taxes et statut</h6>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Devise</label>
                  <PrimevueDropdown v-model="form.currency_code" :options="currencyOptions" optionLabel="label" optionValue="value" class="w-100" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Position devise</label>
                  <PrimevueDropdown v-model="form.currency_position" :options="currencyPositionOptions" optionLabel="label" optionValue="value" class="w-100" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Décimales</label>
                  <PrimevueDropdown v-model="form.decimal_places" :options="decimalPlaceOptions" optionLabel="label" optionValue="value" class="w-100" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Fuseau horaire *</label>
                  <PrimevueDropdown v-model="form.timezone" :options="timezoneOptions" optionLabel="label" optionValue="value" placeholder="Sélectionner un fuseau" class="w-100" />
                </div>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Séparateur décimal</label>
                  <PrimevueDropdown v-model="form.decimal_separator" :options="decimalSeparatorOptions" optionLabel="label" optionValue="value" class="w-100" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Séparateur milliers</label>
                  <PrimevueDropdown v-model="form.thousands_separator" :options="thousandsSeparatorOptions" optionLabel="label" optionValue="value" class="w-100" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Taux TVA (%) *</label>
                  <PrimevueInputNumber v-model="form.tva_rate" class="w-100" mode="decimal" :min="0" :max="100" :maxFractionDigits="2" placeholder="Ex: 0,18" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-semibold">Taux CSS (%) *</label>
                  <PrimevueInputNumber v-model="form.css_rate" class="w-100" mode="decimal" :min="0" :max="100" :maxFractionDigits="2" placeholder="Ex: 0,01" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label small fw-semibold">Texte de pied de page (tickets / factures)</label>
                <PrimevueTextarea v-model.trim="form.footer_note" class="w-100" rows="2" autoResize
                  placeholder="Ex: Merci pour votre confiance, aucun retour sans ticket..." />
              </div>

              <div class="site-opening-hours mb-3">
                <label class="form-label small fw-semibold">Horaires d’ouverture</label>
                <small class="text-muted d-block mb-2">Affichés sur les tickets. La fermeture doit suivre l’ouverture le même jour.</small>
                <div v-for="day in openingHourDays" :key="day.key" :data-opening-day="day.key" class="site-opening-hours__row">
                  <span class="fw-semibold">{{ day.label }}</span>
                  <label class="form-check form-switch mb-0"><input v-model="form.opening_hours[day.key].is_open" class="form-check-input" type="checkbox" /> <span>Ouvert</span></label>
                  <input v-model="form.opening_hours[day.key].opens_at" class="form-control form-control-sm" :aria-label="`${day.label} : ouverture`" :aria-invalid="Boolean(openingHoursErrors[day.key])" :aria-describedby="openingHoursErrors[day.key] ? `opening-hours-error-${day.key}` : undefined" type="time" :disabled="!form.opening_hours[day.key].is_open" />
                  <span>à</span>
                  <input v-model="form.opening_hours[day.key].closes_at" class="form-control form-control-sm" :aria-label="`${day.label} : fermeture`" :aria-invalid="Boolean(openingHoursErrors[day.key])" :aria-describedby="openingHoursErrors[day.key] ? `opening-hours-error-${day.key}` : undefined" type="time" :disabled="!form.opening_hours[day.key].is_open" />
                  <small v-if="openingHoursErrors[day.key]" :id="`opening-hours-error-${day.key}`" class="site-opening-hours__error text-danger" role="alert">{{ openingHoursErrors[day.key] }}</small>
                </div>
              </div>

              <div class="form-check d-flex align-items-center gap-2 ps-0 mb-0">
                <PrimevueCheckbox v-model="form.is_active" binary inputId="siteIsActiveEdit" />
                <label class="form-check-label small" for="siteIsActiveEdit">
                  Site actif
                </label>
              </div>

              <div v-if="ecommerceModuleEnabled" class="form-check d-flex align-items-center gap-2 ps-0 mt-3 mb-0">
                <PrimevueCheckbox v-model="form.ecommerce_enabled" binary inputId="siteEcommerceEnabledEdit" />
                <label class="form-check-label small" for="siteEcommerceEnabledEdit">
                  Activer l’option e-commerce
                </label>
              </div>
            </section>
          </PrimevueTabPanel>

          <PrimevueTabPanel header="Légal">
            <section class="site-form-section">
              <div class="section-heading">
                <span>Étape 4</span>
                <h6>Informations légales</h6>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">N° contribuable / NIF</label>
                  <PrimevueInputText v-model.trim="form.tax_id" class="w-100" />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">Registre de commerce</label>
                  <PrimevueInputText v-model.trim="form.trade_register" class="w-100" />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">Raison sociale</label>
                  <PrimevueInputText v-model.trim="form.legal_name" class="w-100" placeholder="Nom légal de l’entreprise (optionnel)" />
                </div>
              </div>

              <div v-if="form.is_main" class="card border-0 shadow-sm mb-0">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <h6 class="mb-1">
                        <i class="fa fa-image me-1"></i>
                        Logo principal (global)
                      </h6>
                      <small class="text-muted">
                        Un seul logo : celui du site principal. Les autres sites n’enregistrent pas de logo.
                      </small>
                    </div>

                    <span class="badge bg-dark">
                      <i class="fa fa-crown me-1"></i> Site principal
                    </span>
                  </div>

                  <div class="mt-3">
                    <label class="form-label small fw-semibold">Fichier logo</label>
                    <input type="file" class="form-control" accept="image/*" @change="onLogoSelected"
                      :disabled="isSubmitting" />
                    <small class="text-muted d-block mt-1">
                      Formats recommandés : PNG / SVG. Taille conseillée : 512×512.
                    </small>

                    <div v-if="logoPreviewUrl || form.logo_url" class="mt-3 d-flex align-items-center gap-3">
                      <img :src="logoPreviewUrl || form.logo_url" alt="Logo preview" class="logo-preview" />
                      <div class="small">
                        <div class="fw-semibold">Aperçu</div>
                        <button type="button" class="btn btn-outline-danger btn-sm mt-1" @click="clearLogo"
                          :disabled="isSubmitting">
                          <i class="fa fa-xmark me-1" aria-hidden="true"></i>
                          Retirer le logo
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </PrimevueTabPanel>
        </PrimevueTabView>
      </template>

      <template v-else>
        <div class="site-stepper mb-3" role="list" aria-label="Étapes de création du site">
          <button
            v-for="(step, index) in creationSteps"
            :key="step.key"
            type="button"
            class="site-stepper-item"
            :class="creationStepButtonClass(index)"
            :aria-current="currentStep === index ? 'step' : undefined"
            :disabled="isSubmitting"
            @click="goToCreationStep(index)"
          >
            <span class="site-stepper-index">{{ index + 1 }}</span>
            <span class="site-stepper-copy">
              <strong>{{ step.title }}</strong>
              <small>{{ step.description }}</small>
            </span>
          </button>
        </div>

        <section class="site-form-section">
          <div class="section-heading">
            <span>Étape {{ currentStep + 1 }} sur {{ creationSteps.length }}</span>
            <h6>{{ currentCreationStep.title }}</h6>
          </div>

          <template v-if="currentStep === 0">
            <div class="row g-3 mb-3">
              <div class="col-md-5">
                <label class="form-label small fw-semibold">Nom du site / boutique *</label>
                <PrimevueInputText v-model.trim="form.name" class="w-100" placeholder="Ex: Suite241 Libreville" />
                <small class="text-muted">
                  S’affiche dans la navbar et sur les documents (tickets, factures).
                </small>
              </div>

              <div class="col-md-3">
                <label class="form-label small fw-semibold">Code</label>
                <PrimevueInputText v-model.trim="form.code" class="w-100" placeholder="Ex: LBV1" />
                <small class="text-muted">
                  Code interne pour identifier rapidement le site (optionnel).
                </small>
              </div>

              <div class="col-md-4">
                <label class="form-label small fw-semibold">Type de site *</label>
                <PrimevueDropdown
                  v-model="form.site_type"
                  :options="siteTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-100"
                />
                <small class="text-muted">
                  Utilisé pour organiser et présenter la boutique.
                </small>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small fw-semibold">Slogan</label>
              <PrimevueInputText v-model.trim="form.slogan" class="w-100" placeholder="Ex: La qualité au meilleur prix" />
              <small class="text-muted">
                S’affiche sur le site / pages publiques (et éventuellement sur certains documents).
              </small>
            </div>

            <div class="card border-0 shadow-sm mb-0">
              <div class="card-body">
                <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <h6 class="mb-1">
                      <i class="fa fa-image me-1"></i>
                      Image de couverture
                    </h6>
                    <small class="text-muted">
                      Elle sera affichée sur la carte du site dans la grille.
                    </small>
                  </div>
                </div>

                <div class="row g-3 align-items-center mt-1">
                  <div class="col-md-7">
                    <label class="form-label small fw-semibold">Fichier de couverture</label>
                    <input
                      type="file"
                      class="form-control"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      :disabled="isSubmitting"
                      @change="onCoverSelected"
                    />
                    <small class="text-muted d-block mt-1">
                      Formats acceptés : JPG, PNG, WebP, GIF. Taille maximale : 10 Mo.
                    </small>
                  </div>

                  <div class="col-md-5">
                    <div v-if="coverPreviewUrl || form.cover_image_url" class="cover-preview">
                      <img :src="coverPreviewUrl || form.cover_image_url" alt="Aperçu couverture" />
                    </div>
                    <div v-else class="cover-preview cover-preview-empty">
                      <i class="fa fa-image"></i>
                      <span>Aucune image</span>
                    </div>

                    <button
                      v-if="coverPreviewUrl || form.cover_image_url"
                      type="button"
                      class="btn btn-outline-danger btn-sm mt-2"
                      :disabled="isSubmitting"
                      @click="clearCoverImage"
                    >
                      <i class="fa fa-xmark me-1" aria-hidden="true"></i>
                      Retirer la couverture
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="currentStep === 1">
            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label small fw-semibold">Téléphone *</label>
                <PrimevueInputText
                  v-model.trim="form.phone"
                  class="w-100"
                  type="tel"
                  placeholder="Ex: +241 xx xx xx xx"
                  @input="sanitizePhoneField"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label small fw-semibold">WhatsApp</label>
                <PrimevueInputText
                  v-model.trim="form.whatsapp_phone"
                  class="w-100"
                  type="tel"
                  placeholder="Ex: +241 xx xx xx xx"
                  @input="sanitizeWhatsAppField"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label small fw-semibold">Email</label>
                <PrimevueInputText v-model.trim="form.email" type="email" class="w-100" placeholder="Ex: contact@boutique.ga" />
              </div>
            </div>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small fw-semibold">Adresse *</label>
                <PrimevueInputText v-model.trim="form.address" class="w-100" placeholder="Adresse complète" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Ville</label>
                <PrimevueInputText v-model.trim="form.city" class="w-100" placeholder="Ex: Libreville" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Pays</label>
                <PrimevueInputText v-model.trim="form.country" class="w-100" placeholder="Ex: Gabon" />
              </div>
            </div>
          </template>

          <template v-else-if="currentStep === 2">
            <div class="row g-3 mb-3">
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Devise</label>
                <PrimevueDropdown v-model="form.currency_code" :options="currencyOptions" optionLabel="label" optionValue="value" class="w-100" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Position devise</label>
                <PrimevueDropdown v-model="form.currency_position" :options="currencyPositionOptions" optionLabel="label" optionValue="value" class="w-100" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Décimales</label>
                <PrimevueDropdown v-model="form.decimal_places" :options="decimalPlaceOptions" optionLabel="label" optionValue="value" class="w-100" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Fuseau horaire *</label>
                <PrimevueDropdown v-model="form.timezone" :options="timezoneOptions" optionLabel="label" optionValue="value" placeholder="Sélectionner un fuseau" class="w-100" />
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Séparateur décimal</label>
                <PrimevueDropdown v-model="form.decimal_separator" :options="decimalSeparatorOptions" optionLabel="label" optionValue="value" class="w-100" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Séparateur milliers</label>
                <PrimevueDropdown v-model="form.thousands_separator" :options="thousandsSeparatorOptions" optionLabel="label" optionValue="value" class="w-100" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Taux TVA (%) *</label>
                <PrimevueInputNumber v-model="form.tva_rate" class="w-100" mode="decimal" :min="0" :max="100" :maxFractionDigits="2" placeholder="Ex: 0,18" />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold">Taux CSS (%) *</label>
                <PrimevueInputNumber v-model="form.css_rate" class="w-100" mode="decimal" :min="0" :max="100" :maxFractionDigits="2" placeholder="Ex: 0,01" />
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small fw-semibold">Texte de pied de page (tickets / factures)</label>
              <PrimevueTextarea v-model.trim="form.footer_note" class="w-100" rows="2" autoResize
                placeholder="Ex: Merci pour votre confiance, aucun retour sans ticket..." />
            </div>

            <div class="site-opening-hours mb-3">
              <label class="form-label small fw-semibold">Horaires d’ouverture</label>
              <small class="text-muted d-block mb-2">Affichés sur les tickets. La fermeture doit suivre l’ouverture le même jour.</small>
              <div v-for="day in openingHourDays" :key="day.key" :data-opening-day="day.key" class="site-opening-hours__row">
                <span class="fw-semibold">{{ day.label }}</span>
                <label class="form-check form-switch mb-0"><input v-model="form.opening_hours[day.key].is_open" class="form-check-input" type="checkbox" /> <span>Ouvert</span></label>
                <input v-model="form.opening_hours[day.key].opens_at" class="form-control form-control-sm" :aria-label="`${day.label} : ouverture`" :aria-invalid="Boolean(openingHoursErrors[day.key])" :aria-describedby="openingHoursErrors[day.key] ? `opening-hours-error-${day.key}` : undefined" type="time" :disabled="!form.opening_hours[day.key].is_open" />
                <span>à</span>
                <input v-model="form.opening_hours[day.key].closes_at" class="form-control form-control-sm" :aria-label="`${day.label} : fermeture`" :aria-invalid="Boolean(openingHoursErrors[day.key])" :aria-describedby="openingHoursErrors[day.key] ? `opening-hours-error-${day.key}` : undefined" type="time" :disabled="!form.opening_hours[day.key].is_open" />
                <small v-if="openingHoursErrors[day.key]" :id="`opening-hours-error-${day.key}`" class="site-opening-hours__error text-danger" role="alert">{{ openingHoursErrors[day.key] }}</small>
              </div>
            </div>

            <div class="form-check d-flex align-items-center gap-2 ps-0 mb-0">
              <PrimevueCheckbox v-model="form.is_active" binary inputId="siteIsActiveCreate" />
              <label class="form-check-label small" for="siteIsActiveCreate">
                Site actif
              </label>
            </div>

            <div v-if="ecommerceModuleEnabled" class="form-check d-flex align-items-center gap-2 ps-0 mt-3 mb-0">
              <PrimevueCheckbox v-model="form.ecommerce_enabled" binary inputId="siteEcommerceEnabledCreate" />
              <label class="form-check-label small" for="siteEcommerceEnabledCreate">
                Activer l’option e-commerce
              </label>
            </div>
          </template>

          <template v-else>
            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label small fw-semibold">N° contribuable / NIF</label>
                <PrimevueInputText v-model.trim="form.tax_id" class="w-100" />
              </div>
              <div class="col-md-4">
                <label class="form-label small fw-semibold">Registre de commerce</label>
                <PrimevueInputText v-model.trim="form.trade_register" class="w-100" />
              </div>
              <div class="col-md-4">
                <label class="form-label small fw-semibold">Raison sociale</label>
                <PrimevueInputText v-model.trim="form.legal_name" class="w-100" placeholder="Nom légal de l’entreprise (optionnel)" />
              </div>
            </div>

            <div v-if="form.is_main" class="card border-0 shadow-sm mb-0">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h6 class="mb-1">
                      <i class="fa fa-image me-1"></i>
                      Logo principal (global)
                    </h6>
                    <small class="text-muted">
                      Un seul logo : celui du site principal. Les autres sites n’enregistrent pas de logo.
                    </small>
                  </div>

                  <span class="badge bg-dark">
                    <i class="fa fa-crown me-1"></i> Site principal
                  </span>
                </div>

                <div class="mt-3">
                  <label class="form-label small fw-semibold">Fichier logo</label>
                  <input type="file" class="form-control" accept="image/*" @change="onLogoSelected"
                    :disabled="isSubmitting" />
                  <small class="text-muted d-block mt-1">
                    Formats recommandés : PNG / SVG. Taille conseillée : 512×512.
                  </small>

                  <div v-if="logoPreviewUrl || form.logo_url" class="mt-3 d-flex align-items-center gap-3">
                    <img :src="logoPreviewUrl || form.logo_url" alt="Logo preview" class="logo-preview" />
                    <div class="small">
                      <div class="fw-semibold">Aperçu</div>
                      <button type="button" class="btn btn-outline-danger btn-sm mt-1" @click="clearLogo"
                        :disabled="isSubmitting">
                        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
                        Retirer le logo
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </section>
      </template>

      <div class="site-form-actions d-flex flex-wrap justify-content-between gap-2 mt-3">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="$emit('cancel')"
          :disabled="isSubmitting">
          <i class="fa fa-xmark me-1" aria-hidden="true"></i>
          Annuler
        </button>

        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button
            v-if="!isEditMode && currentStep > 0"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="isSubmitting"
            @click="goToPreviousStep"
          >
            <i class="fa fa-arrow-left me-1" aria-hidden="true"></i>
            Précédent
          </button>

          <button
            v-if="!isEditMode && !isLastCreationStep"
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="isSubmitting"
            @click="goToNextStep"
          >
          <i class="fa fa-arrow-right me-1" aria-hidden="true"></i>
          Suivant
          </button>

          <AppLoadingButton
            v-else
            type="submit"
            class="btn btn-primary btn-sm"
            :loading="isSubmitting"
            loading-label="Enregistrement..."
          >
            <i class="fa fa-save me-1" aria-hidden="true"></i>
            {{ isEditMode ? 'Mettre à jour' : 'Créer' }}
          </AppLoadingButton>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
/* eslint-disable */
import { isPhoneInputValid, phoneInputValidationMessage, sanitizePhoneInput } from '@/utils/phone';

const OPENING_HOUR_DAYS = [
  { key: 'monday', label: 'Lundi' }, { key: 'tuesday', label: 'Mardi' }, { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' }, { key: 'friday', label: 'Vendredi' }, { key: 'saturday', label: 'Samedi' }, { key: 'sunday', label: 'Dimanche' },
];
const defaultOpeningHours = () => Object.fromEntries(OPENING_HOUR_DAYS.map(({ key }) => [key, { is_open: false, opens_at: '', closes_at: '' }]));

export default {
  name: 'SiteForm',
  props: {
    mode: { type: String, required: true }, // 'create' | 'edit'
    site: { type: Object, default: null },
    defaultEcommerceEnabled: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        id: null,
        name: '',
        code: '',
        site_type: 'epicerie',
        slogan: '',
        phone: '',
        whatsapp_phone: '',
        email: '',
        address: '',
        city: '',
        country: '',
        tax_id: '',
        trade_register: '',
        currency_code: 'FCFA',
        currency_position: 'after',
        decimal_places: 0,
        decimal_separator: ',',
        thousands_separator: 'space',
        footer_note: '',
        opening_hours: defaultOpeningHours(),
        legal_name: '',
        timezone: 'Africa/Libreville',
        tva_rate: 18,
        css_rate: 1,
        is_active: true,
        ecommerce_enabled: false,

        // ✅ principal
        is_main: false,

        // ✅ logo url existant (si backend renvoie déjà un url)
        logo_url: '',

        // ✅ couverture
        cover_image_url: '',
        remove_cover_image: false,
      },

      // ✅ fichier logo (upload)
      logoFile: null,
      logoPreviewUrl: '',
      coverFile: null,
      coverPreviewUrl: '',

      isSubmitting: false,
      errorMessage: '',
      openingHoursErrors: {},
      currentStep: 0,
      activeEditTab: 0,
      creationSteps: [
        { key: 'identity', title: 'Identité', description: 'Nom, type et couverture' },
        { key: 'contact', title: 'Coordonnées', description: 'Téléphone et adresse' },
        { key: 'settings', title: 'Paramètres', description: 'Devise, taxes et statut' },
        { key: 'legal', title: 'Légal', description: 'Infos administratives' },
      ],
      siteTypeOptions: [
        { label: 'Bars', value: 'bars' },
        { label: 'Épicerie', value: 'epicerie' },
        { label: 'Boutique en ligne', value: 'boutique_en_ligne' },
        { label: 'Autres', value: 'autres' },
        // { label: 'Restaurant', value: 'restaurant' },
      ],
      currencyOptions: [
        { label: 'FCFA', value: 'FCFA' },
        { label: 'XAF', value: 'XAF' },
        { label: 'EUR', value: 'EUR' },
        { label: 'USD', value: 'USD' },
      ],
      currencyPositionOptions: [
        { label: 'Après le montant', value: 'after' },
        { label: 'Avant le montant', value: 'before' },
      ],
      decimalPlaceOptions: [
        { label: '0', value: 0 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
      ],
      decimalSeparatorOptions: [
        { label: 'Virgule', value: ',' },
        { label: 'Point', value: '.' },
      ],
      thousandsSeparatorOptions: [
        { label: 'Espace', value: 'space' },
        { label: 'Point', value: 'dot' },
        { label: 'Virgule', value: 'comma' },
        { label: 'Aucun', value: 'none' },
      ],
      timezoneOptions: [
        { label: 'Africa/Libreville (Gabon)', value: 'Africa/Libreville' },
        { label: 'Africa/Douala', value: 'Africa/Douala' },
        { label: 'Africa/Abidjan', value: 'Africa/Abidjan' },
        { label: 'UTC', value: 'UTC' },
      ],
      openingHourDays: OPENING_HOUR_DAYS,
    };
  },
  computed: {
    ecommerceModuleEnabled() {
      const moduleEnabled = this.$store?.getters?.['runtime/moduleEnabled'];

      return typeof moduleEnabled === 'function' && moduleEnabled('ecommerce');
    },
    isEditMode() {
      return this.mode === 'edit';
    },
    currentCreationStep() {
      return this.creationSteps[this.currentStep] || this.creationSteps[0];
    },
    isLastCreationStep() {
      return this.currentStep === this.creationSteps.length - 1;
    },
  },
  watch: {
    site: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.form = {
            id: newVal.id || null,
            name: newVal.name || '',
            code: newVal.code || '',
            site_type: newVal.site_type === 'restaurant' ? 'epicerie' : (newVal.site_type || 'epicerie'),
            slogan: newVal.slogan || '',
            phone: newVal.phone || '',
            whatsapp_phone: newVal.whatsapp_phone || '',
            email: newVal.email || '',
            address: newVal.address || '',
            city: newVal.city || '',
            country: newVal.country || '',
            tax_id: newVal.tax_id || '',
            trade_register: newVal.trade_register || '',
            currency_code: newVal.currency_code || 'FCFA',
            currency_position: newVal.currency_position || 'after',
            decimal_places: newVal.decimal_places != null ? Number(newVal.decimal_places) : 0,
            decimal_separator: newVal.decimal_separator || ',',
            thousands_separator: this.separatorToKey(newVal.thousands_separator),
            footer_note: newVal.footer_note || '',
            opening_hours: this.normalizeOpeningHours(newVal.opening_hours),
            legal_name: newVal.legal_name || '',
            timezone: newVal.timezone || 'Africa/Libreville',
            tva_rate: newVal.tva_rate != null ? Number(newVal.tva_rate) : 18,
            css_rate: newVal.css_rate != null ? Number(newVal.css_rate) : 1,
            is_active: newVal.is_active !== undefined ? !!newVal.is_active : true,
            ecommerce_enabled: !!newVal.ecommerce_enabled,

            // ✅ principal
            is_main: !!newVal.is_main,

            // ✅ url logo existant (principal)
            logo_url: newVal.logo_url || newVal.logo || newVal.main_logo || '',
            cover_image_url: newVal.cover_image_url || '',
            remove_cover_image: false,
          };

          // reset media selection
          this.logoFile = null;
          this.logoPreviewUrl = '';
          this.coverFile = null;
          this.revokeCoverPreview();
          this.currentStep = 0;
          this.activeEditTab = 0;
          this.errorMessage = '';
          this.openingHoursErrors = {};
        } else {
          this.resetForm();
        }
      },
    },
  },
  beforeUnmount() {
    this.revokePreview();
    this.revokeCoverPreview();
  },
  methods: {
    resetForm() {
      this.revokePreview();
      this.revokeCoverPreview();
      this.logoFile = null;
      this.coverFile = null;

      this.form = {
        id: null,
        name: '',
        code: '',
        site_type: 'epicerie',
        slogan: '',
        phone: '',
        whatsapp_phone: '',
        email: '',
        address: '',
        city: '',
        country: '',
        tax_id: '',
        trade_register: '',
        currency_code: 'FCFA',
        currency_position: 'after',
        decimal_places: 0,
        decimal_separator: ',',
        thousands_separator: 'space',
        footer_note: '',
        opening_hours: defaultOpeningHours(),
        legal_name: '',
        timezone: 'Africa/Libreville',
        tva_rate: 18,
        css_rate: 1,
        is_active: true,
        ecommerce_enabled: this.defaultEcommerceEnabled,
        is_main: false,
        logo_url: '',
        cover_image_url: '',
        remove_cover_image: false,
      };
      this.currentStep = 0;
      this.activeEditTab = 0;
      this.errorMessage = '';
      this.openingHoursErrors = {};
    },

    onLogoSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      // Sécurité: logo uniquement si principal
      if (!this.form.is_main) {
        e.target.value = '';
        alert("Logo : uniquement pour le site principal.");
        return;
      }

      this.logoFile = file;
      this.revokePreview();
      this.logoPreviewUrl = URL.createObjectURL(file);
    },
    normalizeOpeningHours(value) {
      const defaults = defaultOpeningHours();
      if (!value || typeof value !== 'object') return defaults;

      OPENING_HOUR_DAYS.forEach(({ key }) => {
        const hours = value[key] || {};
        defaults[key] = {
          is_open: [true, 1, '1', 'true'].includes(hours.is_open),
          opens_at: this.normalizeOpeningTime(hours.opens_at),
          closes_at: this.normalizeOpeningTime(hours.closes_at),
        };
      });
      return defaults;
    },

    normalizeOpeningTime(value) {
      const time = String(value ?? '').trim();
      const parts = time.match(/^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/);
      return parts && Number(parts[1]) < 24 ? `${parts[1].padStart(2, '0')}:${parts[2]}` : time;
    },

    openingHoursPayload() {
      const hours = this.normalizeOpeningHours(this.form.opening_hours);
      Object.values(hours).forEach((day) => {
        if (!day.is_open) {
          day.opens_at = null;
          day.closes_at = null;
        }
      });
      return hours;
    },

    validateOpeningHours() {
      const errors = {};
      const validTime = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
      Object.entries(this.openingHoursPayload()).forEach(([day, hours]) => {
        if (!hours.is_open) return;
        if (!hours.opens_at || !hours.closes_at) {
          errors[day] = 'Renseignez les heures d’ouverture et de fermeture de ce jour ouvert.';
        } else if (!validTime.test(hours.opens_at) || !validTime.test(hours.closes_at)) {
          errors[day] = 'Les heures doivent être valides au format HH:mm.';
        } else if (hours.opens_at >= hours.closes_at) {
          errors[day] = 'L’heure de fermeture doit être postérieure à l’heure d’ouverture, le même jour.';
        }
      });
      this.openingHoursErrors = errors;
      if (Object.keys(errors).length) {
        return this.setValidationError('Vérifiez les horaires des jours indiqués ci-dessous.', 2);
      }
      return true;
    },

    clearLogo() {
      this.revokePreview();
      this.logoFile = null;
      this.logoPreviewUrl = '';
      // on laisse logo_url tel quel, mais si tu veux forcer suppression backend:
      // this.form.logo_url = '';
    },

    onCoverSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      this.coverFile = file;
      this.form.remove_cover_image = false;
      this.revokeCoverPreview();
      this.coverPreviewUrl = URL.createObjectURL(file);
    },

    clearCoverImage() {
      const hadPersistedCover = !!this.form.cover_image_url;

      this.coverFile = null;
      this.revokeCoverPreview();
      this.form.cover_image_url = '';
      this.form.remove_cover_image = hadPersistedCover;
    },

    separatorToKey(value) {
      if (value === ' ') return 'space';
      if (value === '.') return 'dot';
      if (value === ',') return 'comma';
      if (value === '') return 'none';
      return value || 'space';
    },

    revokePreview() {
      if (this.logoPreviewUrl) {
        try { URL.revokeObjectURL(this.logoPreviewUrl); } catch (e) { }
      }
      this.logoPreviewUrl = '';
    },

    revokeCoverPreview() {
      if (this.coverPreviewUrl) {
        try { URL.revokeObjectURL(this.coverPreviewUrl); } catch (e) { }
      }
      this.coverPreviewUrl = '';
    },

    sanitizePhoneField(event) {
      this.form.phone = sanitizePhoneInput(event?.target?.value ?? this.form.phone);
    },

    sanitizeWhatsAppField(event) {
      this.form.whatsapp_phone = sanitizePhoneInput(event?.target?.value ?? this.form.whatsapp_phone);
    },

    isBlank(value) {
      return String(value ?? '').trim() === '';
    },

    isNumericFieldEmpty(value) {
      return value === null || value === undefined || value === '';
    },

    setValidationError(message, stepIndex) {
      this.errorMessage = message;

      if (this.isEditMode) {
        this.activeEditTab = stepIndex;
      } else {
        this.currentStep = stepIndex;
      }

      return false;
    },

    validateStep(stepIndex) {
      if (stepIndex === 0) {
        if (this.isBlank(this.form.name)) {
          return this.setValidationError('Veuillez renseigner le nom du site.', stepIndex);
        }

        if (!['bars', 'epicerie', 'restaurant', 'boutique_en_ligne', 'autres'].includes(this.form.site_type)) {
          return this.setValidationError('Veuillez sélectionner un type de site valide.', stepIndex);
        }

        return true;
      }

      if (stepIndex === 1) {
        if (this.isBlank(this.form.phone)) {
          return this.setValidationError('Veuillez renseigner le téléphone du site.', stepIndex);
        }

        if (!isPhoneInputValid(this.form.phone)) {
          return this.setValidationError(phoneInputValidationMessage('téléphone du site'), stepIndex);
        }

        if (!isPhoneInputValid(this.form.whatsapp_phone)) {
          return this.setValidationError(phoneInputValidationMessage('numéro WhatsApp du site'), stepIndex);
        }

        if (this.isBlank(this.form.address)) {
          return this.setValidationError('Veuillez renseigner l’adresse du site.', stepIndex);
        }

        return true;
      }

      if (stepIndex === 2) {
        if (this.isBlank(this.form.timezone)) {
          return this.setValidationError('Veuillez sélectionner le fuseau horaire du site.', stepIndex);
        }

        if (this.isNumericFieldEmpty(this.form.tva_rate)) {
          return this.setValidationError('Veuillez renseigner le taux TVA du site.', stepIndex);
        }

        if (this.isNumericFieldEmpty(this.form.css_rate)) {
          return this.setValidationError('Veuillez renseigner le taux CSS du site.', stepIndex);
        }

        return this.validateOpeningHours();
      }

      return true;
    },

    validateAllSteps() {
      for (let index = 0; index < this.creationSteps.length; index += 1) {
        if (!this.validateStep(index)) {
          return false;
        }
      }

      return true;
    },

    goToPreviousStep() {
      this.errorMessage = '';
      this.openingHoursErrors = {};
      this.currentStep = Math.max(0, this.currentStep - 1);
    },

    goToNextStep() {
      this.errorMessage = '';
      this.openingHoursErrors = {};

      if (this.validateStep(this.currentStep)) {
        this.currentStep = Math.min(this.creationSteps.length - 1, this.currentStep + 1);
      }
    },

    goToCreationStep(index) {
      if (index === this.currentStep || index < 0 || index >= this.creationSteps.length) {
        return;
      }

      if (index < this.currentStep) {
        this.errorMessage = '';
        this.openingHoursErrors = {};
        this.currentStep = index;
        return;
      }

      for (let stepIndex = this.currentStep; stepIndex < index; stepIndex += 1) {
        if (!this.validateStep(stepIndex)) {
          return;
        }
      }

      this.errorMessage = '';
      this.openingHoursErrors = {};
      this.currentStep = index;
    },

    creationStepButtonClass(index) {
      return {
        'is-active': this.currentStep === index,
        'is-complete': this.currentStep > index,
      };
    },

    async handleSubmit() {
      if (this.isSubmitting) return;

      this.errorMessage = '';
      this.openingHoursErrors = {};

      if (!this.validateAllSteps()) {
        return;
      }

      this.form.phone = sanitizePhoneInput(this.form.phone);
      this.form.whatsapp_phone = sanitizePhoneInput(this.form.whatsapp_phone);
      this.isSubmitting = true;

      try {
        // ✅ On envoie en multipart si logo/couverture présent,
        // sinon JSON classique.
        const mustSendLogo = this.form.is_main && !!this.logoFile;
        const mustSendCover = !!this.coverFile || !!this.form.remove_cover_image;

        if (mustSendLogo || mustSendCover) {
          const fd = new FormData();

          fd.append('name', this.form.name);
          if (this.form.code) fd.append('code', this.form.code);
          fd.append('site_type', this.form.site_type || 'epicerie');
          if (this.form.slogan) fd.append('slogan', this.form.slogan);

          fd.append('phone', this.form.phone);
          fd.append('whatsapp_phone', this.form.whatsapp_phone || '');
          if (this.form.email) fd.append('email', this.form.email);

          fd.append('address', this.form.address);
          if (this.form.city) fd.append('city', this.form.city);
          if (this.form.country) fd.append('country', this.form.country);

          if (this.form.tax_id) fd.append('tax_id', this.form.tax_id);
          if (this.form.trade_register) fd.append('trade_register', this.form.trade_register);

          fd.append('currency_code', this.form.currency_code || 'FCFA');
          fd.append('currency_position', this.form.currency_position || 'after');
          fd.append('decimal_places', String(this.form.decimal_places ?? 0));
          fd.append('decimal_separator', this.form.decimal_separator || ',');
          fd.append('thousands_separator', this.form.thousands_separator || 'space');
          if (this.form.footer_note) fd.append('footer_note', this.form.footer_note);
          fd.append('opening_hours', JSON.stringify(this.openingHoursPayload()));
          if (this.form.legal_name) fd.append('legal_name', this.form.legal_name);

          fd.append('timezone', this.form.timezone);
          fd.append('tva_rate', String(this.form.tva_rate ?? 0));
          fd.append('css_rate', String(this.form.css_rate ?? 0));
          fd.append('is_active', this.form.is_active ? '1' : '0');
          if (this.ecommerceModuleEnabled) {
            fd.append('ecommerce_enabled', this.form.ecommerce_enabled ? '1' : '0');
          }

          if (mustSendCover && this.form.remove_cover_image) {
            fd.append('remove_cover_image', '1');
          }

          if (this.coverFile) {
            fd.append('cover_image', this.coverFile);
          }

          if (mustSendLogo) {
            fd.append('logo', this.logoFile);
          }

          if (this.isEditMode && this.form.id) {
            // Laravel: si tu veux rester en PUT avec multipart, tu peux envoyer _method=PUT
            fd.append('_method', 'PUT');
            await this.$api.post(`/shops/${this.form.id}`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } else {
            await this.$api.post('/shops', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        } else {
          const payload = {
            name: this.form.name,
            code: this.form.code || null,
            site_type: this.form.site_type || 'epicerie',
            slogan: this.form.slogan || null,
            phone: this.form.phone,
            whatsapp_phone: this.form.whatsapp_phone || null,
            email: this.form.email || null,
            address: this.form.address,
            city: this.form.city || null,
            country: this.form.country || null,
            tax_id: this.form.tax_id || null,
            trade_register: this.form.trade_register || null,
            currency_code: this.form.currency_code || 'FCFA',
            currency_position: this.form.currency_position || 'after',
            decimal_places: this.form.decimal_places ?? 0,
            decimal_separator: this.form.decimal_separator || ',',
            thousands_separator: this.form.thousands_separator || 'space',
            footer_note: this.form.footer_note || null,
            opening_hours: this.openingHoursPayload(),
            legal_name: this.form.legal_name || null,
            timezone: this.form.timezone,
            tva_rate: this.form.tva_rate,
            css_rate: this.form.css_rate,
            is_active: this.form.is_active,
          };

          // Hidden module fields are omitted so an edit cannot erase a stored publication choice.
          if (this.ecommerceModuleEnabled) {
            payload.ecommerce_enabled = this.form.ecommerce_enabled;
          }

          if (this.isEditMode && this.form.id) {
            await this.$api.put(`/shops/${this.form.id}`, payload);
          } else {
            await this.$api.post('/shops', payload);
          }
        }

        if (this.isEditMode
          && Number(this.form.id) === Number(this.$store.getters['shop/currentShopId'])) {
          // Refreshing the current-site snapshot makes the settings navigation react immediately.
          await this.$store.dispatch('shop/fetchInfo');
        }

        this.$emit('saved');
      } catch (error) {
        console.error('Erreur enregistrement site', error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          const dayErrors = {};
          Object.entries(errors).forEach(([field, messages]) => {
            const day = field.split('.')[1];
            if (field.startsWith('opening_hours.') && OPENING_HOUR_DAYS.some(({ key }) => key === day)) {
              dayErrors[day] = [dayErrors[day], ...[].concat(messages)].filter(Boolean).join(' ');
            }
          });
          this.openingHoursErrors = dayErrors;
          const message = Object.values(errors).flat().join(' ');
          if (Object.keys(dayErrors).length || errors.opening_hours) {
            this.setValidationError(message, 2);
          } else {
            this.errorMessage = message;
          }
        } else {
          this.errorMessage = 'Une erreur est survenue lors de la sauvegarde du site.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.site-stepper {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.site-stepper-item {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.14);
  border-radius: 8px;
  color: #475569;
  display: flex;
  gap: 0.75rem;
  min-height: 74px;
  padding: 0.85rem;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease;
}

.site-stepper-item:hover:not(:disabled),
.site-stepper-item.is-active {
  border-color: #2563eb;
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12);
  color: #0f172a;
}

.site-stepper-item.is-complete {
  background: #f8fbff;
  border-color: rgba(37, 99, 235, 0.3);
}

.site-stepper-index {
  align-items: center;
  background: #e2e8f0;
  border-radius: 50%;
  color: #0f172a;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.8rem;
  font-weight: 800;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.site-stepper-item.is-active .site-stepper-index,
.site-stepper-item.is-complete .site-stepper-index {
  background: #2563eb;
  color: #ffffff;
}

.site-stepper-copy {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.site-stepper-copy strong {
  font-size: 0.88rem;
  line-height: 1.2;
}

.site-stepper-copy small {
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.25;
}

.site-form-section {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 8px;
  padding: 1rem;
}

.section-heading {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
}

.section-heading span {
  color: #2563eb;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.section-heading h6 {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
  margin: 0.2rem 0 0;
}

.site-form-tabs :deep(.p-tabview-nav) {
  border-color: rgba(15, 23, 42, 0.1);
}

.site-form-tabs :deep(.p-tabview-panels) {
  padding: 1rem 0 0;
}

.site-form-actions {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  padding-top: 1rem;
}

.site-opening-hours { border-top: 1px solid rgba(15, 23, 42, 0.1); padding-top: 0.85rem; }
.site-opening-hours__row { align-items: center; display: grid; gap: 0.5rem; grid-template-columns: 6.4rem 6.5rem minmax(6rem, 1fr) auto minmax(6rem, 1fr); margin-top: 0.45rem; }
.site-opening-hours__error { grid-column: 1 / -1; }
.site-opening-hours__row .form-check-input { margin-left: 0; }

.logo-preview {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  height: 56px;
  object-fit: cover;
  width: 56px;
}

.cover-preview {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid rgba(33, 37, 41, 0.12);
  border-radius: 0.5rem;
  display: flex;
  height: 120px;
  justify-content: center;
  overflow: hidden;
  width: 100%;
}

.cover-preview img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.cover-preview-empty {
  color: #6c757d;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.875rem;
}

.cover-preview-empty i {
  font-size: 1.5rem;
}

@media (max-width: 991.98px) {
  .site-stepper {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .site-opening-hours__row { grid-template-columns: 1fr 1fr; }
  .site-opening-hours__row > span { grid-column: 1 / -1; }
  .site-stepper {
    grid-template-columns: 1fr;
  }
}
</style>
